import { useCallback, useMemo, useRef, useState } from 'react';

import { useTranslation } from '@/i18n';
import { useUI } from '@/hooks/ui/useUI';
import { usePagePermissions } from '@/hooks/authorization/usePagePermissions';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
} from '@/hooks/ui/usePaginatedList';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import {
  useEmployeeDimensionalFilter,
  type DimensionalFilterConfig,
  type UseEmployeeDimensionalFilterResult,
} from '@/hooks/attendance/common/useEmployeeDimensionalFilter';
import { useMarksReportExport } from '@/hooks/attendance/report/useMarksReportExport';
import { marksReportService } from '@/api/domains/attendance/services/marksReport.service';
import { markingEventService } from '@/api/domains/catalog/eventType/eventType.service';
import { locationsCatalogService } from '@/api/domains/catalog/locations/locations.service';
import { companyControlsService } from '@/api/domains/catalog/companyControls/companyControls.service';
import { formatDateToYYYYMMDD, formatRut } from '@/utils';
import { semanticColors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import type { MarkReport } from '@/api/domains/attendance/types/marksReport.types';
import type { EmployeeFilteredStatus } from '@/api/domains/employee/types';
import type { UIExportFormat } from '@/types/fileExport.types';
import type { DateRangeValue } from '@/components/ui/filters/DateRangePicker';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import type { BulkActionItem, TableAction, TableColumn } from '@/components/ui/tables/Table.types';


const DEFAULT_PAGE_SIZE = 25;
// 6 days back + today = a 7-day window in total.
const DEFAULT_RANGE_DAYS = 6;
const ALL_FILTER_VALUE = 'todos';
// Ajuste masivo de marcaje (ASIS-5313): tope de marcajes que pueden seleccionarse a la vez,
// cross-página, para acotar el tamaño del payload/lote procesado por el backend.
const MAX_BULK_ADJUST_MARKS = 100;

// "Todo" default for the advanced panel's employee status, per ASIS-5308 AC — differs from the
// 'vigente' default used elsewhere (e.g. AttendanceMarksAdmin).
const DEFAULT_ADVANCED_EMPLOYEE_STATUS: EmployeeFilteredStatus = 'todos';

// dimensionalConfig excludes `location`: the employee-assigned location is not part of this
// screen's advanced filters — mark location is already covered by the quick filter row.
const DIMENSIONAL_CONFIG: DimensionalFilterConfig = {
  company: true,
  area: true,
  position: true,
  costCenter: true,
  contractModality: true,
  securityGroup: true,
  employeeStatus: true,
  employee: true,
};

function joinOrUndefined(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined;
}

// Column keys map 1:1 to backend order fields. Declared explicitly so the
// backend validator never rejects an unmapped orderBy value.
const ORDER_BY_MAP: Record<string, string> = {
  rut: 'employeeTaxIdentificationNumber',
  fullName: 'fullName',
  companyName: 'companyName',
  contractNumber: 'contractNumber',
  positionName: 'positionName',
  markType: 'markType',
  eventName: 'eventName',
  workdayDate: 'workdayDate',
  markTime: 'markTime',
  locationName: 'locationName',
};

interface FilterState {
  dateFrom: string;
  dateTo: string;
  eventId: string;
  locationId: string;
  controlId: string;
  // Advanced (dimensional) filters — only committed to this state when the user presses Buscar.
  employeeIds: number[];
  companyIds: string[];
  areaIds: string[];
  positionIds: string[];
  costCenterIds: string[];
  contractModalityIds: string[];
  groupIds: string[];
  employeeStatus: EmployeeFilteredStatus;
}

export interface UseMarksReportResult {
  rows: MarkReport[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  columns: TableColumn<MarkReport>[];
  actions: TableAction<MarkReport>[];
  onSearch: (term: string) => void;
  /**
   * Texto de búsqueda vigente (ya debounceado). La página lo pasa como `initialSearchValue`
   * para que la caja no se vacíe visualmente cuando `tableSelectionKey` remonta la tabla.
   */
  searchText: string;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  onDateRangeChange: (range: DateRangeValue) => void;
  dateFrom: string;
  dateTo: string;
  filterDefinitions: SimpleFilter[];
  filterValues: Record<string, string>;
  onFilterChange: (key: string, value: string | string[]) => void;
  isLoading: boolean;
  canDownload: boolean;
  onDownload: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
  // Advanced (dimensional) filters panel
  dimensionalConfig: DimensionalFilterConfig;
  dimensionalFilter: UseEmployeeDimensionalFilterResult;
  onAdvancedSearch: () => void;
  // Bulk resend support (ASIS-5311) / bulk adjust mark (ASIS-5313)
  bulkActions: BulkActionItem[];
  tableSelectionKey: string;
  onSelectionChange: (selected: MarkReport[]) => void;
  selectedMarks: MarkReport[];
  isRowSelectable: (row: MarkReport) => boolean;
}

export function useMarksReport(): UseMarksReportResult {
  const { t: translate } = useTranslation();
  const { showApiError, showSuccess, showError, showWarning, openSidePanel } = useUI();
  const { can } = usePagePermissions();

  // Lazy initial filters: default to the last 7 days (today-6 → today).
  const initialFilters = useMemo<FilterState>(() => {
    const today = new Date();
    const fromDate = new Date(today);
    fromDate.setDate(fromDate.getDate() - DEFAULT_RANGE_DAYS);
    return {
      dateFrom: formatDateToYYYYMMDD(fromDate),
      dateTo: formatDateToYYYYMMDD(today),
      eventId: ALL_FILTER_VALUE,
      locationId: ALL_FILTER_VALUE,
      controlId: ALL_FILTER_VALUE,
      employeeIds: [],
      companyIds: [],
      areaIds: [],
      positionIds: [],
      costCenterIds: [],
      contractModalityIds: [],
      groupIds: [],
      employeeStatus: DEFAULT_ADVANCED_EMPLOYEE_STATUS,
    };
  }, []);

  const dimensionalFilter = useEmployeeDimensionalFilter(
    DIMENSIONAL_CONFIG,
    DEFAULT_ADVANCED_EMPLOYEE_STATUS,
  );

  const { options: eventOptions } = useCatalogLoader(
    (signal) => markingEventService.getMarkingEvents(signal),
    (event) => ({ value: String(event.id), label: event.description }),
  );

  // status=all: los marcajes históricos pueden pertenecer a ubicaciones hoy inactivas.
  const { options: locationOptions } = useCatalogLoader(
    (signal) => locationsCatalogService.getLocations(signal, { includeInactive: true }),
    (location) => ({ value: String(location.id), label: location.name }),
  );

  // Catálogo de controles (dispositivos) de la empresa. Sin onError: si la empresa
  // no tiene controles configurados (404), el dropdown queda solo con "Todos".
  const { options: controlOptions } = useCatalogLoader(
    (signal) => companyControlsService.getCompanyControls(signal),
    (control) => ({ value: String(control.id), label: control.description }),
  );

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<FilterState>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<MarkReport> | null> => {
      const response = await marksReportService.list(
        {
          page: params.page,
          pageSize: params.pageSize,
          dateFrom: params.filters.dateFrom,
          dateTo: params.filters.dateTo,
          eventIds:
            params.filters.eventId === ALL_FILTER_VALUE ? undefined : params.filters.eventId,
          markLocationIds:
            params.filters.locationId === ALL_FILTER_VALUE ? undefined : params.filters.locationId,
          controlIds:
            params.filters.controlId === ALL_FILTER_VALUE ? undefined : params.filters.controlId,
          employeeIds: joinOrUndefined(params.filters.employeeIds.map(String)),
          companyIds: joinOrUndefined(params.filters.companyIds),
          areaIds: joinOrUndefined(params.filters.areaIds),
          positionIds: joinOrUndefined(params.filters.positionIds),
          costCenterIds: joinOrUndefined(params.filters.costCenterIds),
          contractModalityIds: joinOrUndefined(params.filters.contractModalityIds),
          groupIds: joinOrUndefined(params.filters.groupIds),
          employeeStatus: params.filters.employeeStatus,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        showApiError(response, translate('attendance:marksReport.messages.loadError'));
        return null;
      }

      return {
        items: response.data.items,
        totalRecords: response.data.pagination.totalRecords,
      };
    },
    [showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    debouncedSearchText,
    filters,
    setFilters,
    refetch,
  } = usePaginatedList<MarkReport, FilterState>({
    fetchFn,
    initialFilters,
    initialSort: { key: 'workdayDate', direction: 'DESC' },
    initialPageSize: DEFAULT_PAGE_SIZE,
    orderByMap: ORDER_BY_MAP,
  });

  const columns: TableColumn<MarkReport>[] = useMemo(() => [
    {
      key: 'pendingIndicator',
      header: '',
      sortable: false,
      render: (row: MarkReport) => (
        row.hasPendingAdjustment ? (
          // El title (tooltip) se mantiene en el contenedor porque el componente Text no
          // reenvía atributos nativos como title; el dot en sí es un Text con estilo de punto.
          <span title={translate('attendance:marksReport.adjust.pendingIndicator.legend')}>
            <Text
              as="span"
              data-testid="pending-adjustment-indicator"
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: semanticColors.status.warning,
              }}
            >
              {null}
            </Text>
          </span>
        ) : null
      ),
    },
    {
      key: 'rut',
      header: translate('attendance:marksReport.columns.rut'),
      sortable: true,
      render: (row: MarkReport) => formatRut(row.rut),
    },
    {
      key: 'fullName',
      header: translate('attendance:marksReport.columns.employee'),
      sortable: true,
    },
    {
      key: 'companyName',
      header: translate('attendance:marksReport.columns.company'),
      sortable: true,
    },
    {
      key: 'contractNumber',
      header: translate('attendance:marksReport.columns.contract'),
      sortable: true,
    },
    {
      key: 'positionName',
      header: translate('attendance:marksReport.columns.position'),
      sortable: true,
    },
    {
      key: 'markType',
      header: translate('attendance:marksReport.columns.type'),
      sortable: true,
    },
    {
      key: 'eventName',
      header: translate('attendance:marksReport.columns.event'),
      sortable: true,
    },
    {
      key: 'workdayDate',
      header: translate('attendance:marksReport.columns.workday'),
      sortable: true,
    },
    {
      key: 'markTime',
      header: translate('attendance:marksReport.columns.time'),
      sortable: true,
    },
    {
      key: 'locationName',
      header: translate('attendance:marksReport.columns.location'),
      sortable: true,
    },
  ], [translate]);

  const handleViewDetail = useCallback((row: MarkReport) => {
    openSidePanel({
      component: 'MarkDetailPanel',
      title: translate('attendance:marksReport.detail.title'),
      props: { record: row },
    });
  }, [openSidePanel, translate]);

  // Ajuste de marcaje (individual ASIS-5312 / masivo ASIS-5313): un marcaje de hoy con hora
  // futura respecto al reloj del cliente no puede ajustarse — se reutiliza tanto para bloquear
  // la acción de fila individual como para acotar la selección de filas del ajuste masivo.
  const isMarkAdjustable = useCallback((row: MarkReport): boolean => {
    const now = new Date();
    const todayLabel = formatDateToYYYYMMDD(now).split('-').reverse().join('/');
    const markDayLabel = row.markDate ?? row.workdayDate;

    if (markDayLabel === todayLabel) {
      const nowTimeLabel = now.toTimeString().slice(0, 8);
      if (row.markTime > nowTimeLabel) return false;
    }

    return true;
  }, []);

  // Ajuste de marcaje (ASIS-5312): abre panel lateral salvo que el marcaje sea de hoy con
  // hora futura respecto al reloj del cliente — en ese caso se muestra un toast y no se abre.
  const handleAdjustMark = useCallback((row: MarkReport) => {
    if (!isMarkAdjustable(row)) {
      showError(translate('attendance:marksReport.adjust.messages.futureTime'));
      return;
    }

    openSidePanel({
      component: 'AdjustMarkPanel',
      title: translate('attendance:marksReport.adjust.title'),
      props: {
        record: row,
        onSubmit: () => refetch(),
      },
    });
  }, [isMarkAdjustable, openSidePanel, translate, showError, refetch]);

  // Re-envío del soporte de marcaje (ASIS-5310): POST inmediato al backend, sin confirmación.
  // El correo destino se resuelve en el backend (EmailPersonal del colaborador).
  const handleResendSupport = useCallback(async (row: MarkReport) => {
    const response = await marksReportService.resendSupport(row.id);
    if (!response.success) {
      showApiError(response, translate('attendance:marksReport.resend.error'));
      return;
    }
    showSuccess(translate('attendance:marksReport.resend.success'));
  }, [showApiError, showSuccess, translate]);

  // Re-envío masivo del soporte de marcaje (ASIS-5311): selección por checkboxes de la
  // tabla, POST inmediato al backend, sin confirmación (igual que el reenvío individual).
  const [selectedMarks, setSelectedMarks] = useState<MarkReport[]>([]);
  const [selectionVersion, setSelectionVersion] = useState(0);
  // Ref (not state) so the guard below reads the up-to-date value synchronously — a state
  // flag would still read stale (false) if the action is invoked twice before React re-renders.
  const isSubmittingBulkRef = useRef(false);

  // Solo selectionVersion: se remonta la tabla (y se limpia el Set interno) únicamente
  // tras un envío masivo exitoso, no al paginar — así la selección persiste entre páginas.
  const tableSelectionKey = useMemo(() => String(selectionVersion), [selectionVersion]);

  // La Table emite solo las filas seleccionadas de la página visible. Reemplazamos el
  // subconjunto de la página actual y conservamos las selecciones de otras páginas, para
  // que selección Y deselección cross-página funcionen (una unión pura no permitiría desmarcar).
  //
  // Tope de 100 (ASIS-5313): si el conjunto resultante supera el máximo, se recorta a los
  // primeros MAX_BULK_ADJUST_MARKS (favoreciendo lo ya seleccionado en otras páginas) en vez
  // de descartar el cambio completo — así el usuario conserva selección hasta el tope en lugar
  // de perderla por completo, y se le avisa por qué no quedó todo lo que intentó marcar.
  const onSelectionChange = useCallback((selected: MarkReport[]) => {
    const currentPageIds = new Set(rows.map((row) => row.id));
    const fromOtherPages = selectedMarks.filter((mark) => !currentPageIds.has(mark.id));
    const merged = [...fromOtherPages, ...selected];

    if (merged.length > MAX_BULK_ADJUST_MARKS) {
      showWarning(translate('attendance:marksReport.adjustBulk.limitReached'));
      setSelectedMarks(merged.slice(0, MAX_BULK_ADJUST_MARKS));
      return;
    }

    setSelectedMarks(merged);
  }, [rows, selectedMarks, showWarning, translate]);

  const handleBulkResendSupport = useCallback(async () => {
    if (isSubmittingBulkRef.current || selectedMarks.length === 0) return;

    isSubmittingBulkRef.current = true;
    try {
      const response = await marksReportService.resendSupportBulk(
        selectedMarks.map((mark) => mark.id),
      );

      if (!response.success) {
        showApiError(response, translate('attendance:marksReport.resendBulk.error'));
        return;
      }

      showSuccess(translate('attendance:marksReport.resendBulk.success'));
      setSelectedMarks([]);
      setSelectionVersion((version) => version + 1);
    } finally {
      isSubmittingBulkRef.current = false;
    }
  }, [selectedMarks, showApiError, showSuccess, translate]);

  // Ajuste masivo de marcaje (ASIS-5313): abre el panel lateral con la selección vigente.
  // Igual que el ajuste individual, requiere permiso de edición (no solo vista).
  const handleBulkAdjustMark = useCallback(() => {
    if (selectedMarks.length === 0) return;

    openSidePanel({
      component: 'BulkAdjustMarkPanel',
      title: translate('attendance:marksReport.adjustBulk.title'),
      props: {
        records: selectedMarks,
        onSubmit: () => {
          setSelectedMarks([]);
          setSelectionVersion((version) => version + 1);
          refetch();
        },
      },
    });
  }, [selectedMarks, openSidePanel, translate, refetch]);

  const bulkActions: BulkActionItem[] = useMemo(() => {
    const items: BulkActionItem[] = [];

    if (can('view')) {
      items.push({
        label: translate('attendance:marksReport.resendBulk.action'),
        icon: 'paperPlane',
        onClick: () => { void handleBulkResendSupport(); },
      });
    }

    if (can('edit')) {
      items.push({
        label: translate('attendance:marksReport.adjustBulk.action'),
        icon: 'editAlt',
        onClick: handleBulkAdjustMark,
      });
    }

    return items;
  }, [can, translate, handleBulkResendSupport, handleBulkAdjustMark]);

  const actions: TableAction<MarkReport>[] = useMemo(() => [
    {
      id: 'viewDetail',
      label: translate('common:tableActions.detail'),
      icon: 'eye',
      onClick: handleViewDetail,
      hidden: () => !can('view'),
    },
    {
      id: 'resendSupport',
      label: translate('attendance:marksReport.actions.resendSupport'),
      icon: 'paperPlane',
      onClick: handleResendSupport,
      hidden: () => !can('view'),
    },
    {
      id: 'adjustMark',
      label: translate('attendance:marksReport.actions.adjustMark'),
      icon: 'editAlt',
      onClick: handleAdjustMark,
      hidden: () => !can('edit'),
    },
  ], [handleViewDetail, handleResendSupport, handleAdjustMark, can, translate]);

  // Descarga (ASIS-5314): mismos filtros activos que el listado — incluido searchText — sin
  // paginación. Estrategia híbrida sync/async delegada en un hook dedicado, igual que el resto
  // de export híbridos (useAuditExport / useIncidentManagementExport).
  const { isExporting, onExport: onDownload } = useMarksReportExport({
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    eventId: filters.eventId,
    locationId: filters.locationId,
    controlId: filters.controlId,
    employeeIds: filters.employeeIds,
    companyIds: filters.companyIds,
    areaIds: filters.areaIds,
    positionIds: filters.positionIds,
    costCenterIds: filters.costCenterIds,
    contractModalityIds: filters.contractModalityIds,
    groupIds: filters.groupIds,
    employeeStatus: filters.employeeStatus,
    searchText: debouncedSearchText,
  });

  const onDateRangeChange = useCallback((range: DateRangeValue) => {
    setFilters((prev) => ({
      ...prev,
      dateFrom: range.from ?? '',
      dateTo: range.to ?? '',
    }));
  }, [setFilters]);

  const onFilterChange = useCallback((key: string, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [key]: value as string }));
  }, [setFilters]);

  const allOption = useMemo(
    () => ({ value: ALL_FILTER_VALUE, label: translate('attendance:marksReport.filters.all') }),
    [translate],
  );

  const filterDefinitions = useMemo<SimpleFilter[]>(() => [
    {
      key: 'eventId',
      label: translate('attendance:marksReport.filters.event'),
      options: [allOption, ...eventOptions],
      defaultValue: ALL_FILTER_VALUE,
    },
    {
      key: 'locationId',
      label: translate('attendance:marksReport.filters.location'),
      options: [allOption, ...locationOptions],
      defaultValue: ALL_FILTER_VALUE,
    },
    {
      key: 'controlId',
      label: translate('attendance:marksReport.filters.device'),
      options: [allOption, ...controlOptions],
      defaultValue: ALL_FILTER_VALUE,
    },
  ], [translate, allOption, eventOptions, locationOptions, controlOptions]);

  const filterValues = useMemo<Record<string, string>>(() => ({
    eventId: filters.eventId,
    locationId: filters.locationId,
    controlId: filters.controlId,
  }), [filters.eventId, filters.locationId, filters.controlId]);

  // Buscar: commits the advanced (dimensional) panel's staged selections into the fetch
  // filters. Quick filters and the date range keep applying immediately, unaffected by this.
  const onAdvancedSearch = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      employeeIds: dimensionalFilter.selectedEmployeeIds,
      companyIds: dimensionalFilter.dimensionalFilters.companyIds,
      areaIds: dimensionalFilter.dimensionalFilters.areaIds,
      positionIds: dimensionalFilter.dimensionalFilters.positionIds,
      costCenterIds: dimensionalFilter.dimensionalFilters.costCenterIds,
      contractModalityIds: dimensionalFilter.dimensionalFilters.contractModalityIds,
      groupIds: dimensionalFilter.dimensionalFilters.groupIds,
      employeeStatus: dimensionalFilter.dimensionalFilters.status,
    }));
  }, [setFilters, dimensionalFilter.selectedEmployeeIds, dimensionalFilter.dimensionalFilters]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    columns,
    actions,
    onSearch,
    searchText: debouncedSearchText,
    onSortChange,
    onDateRangeChange,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    filterDefinitions,
    filterValues,
    onFilterChange,
    isLoading: loading,
    canDownload: total > 0,
    onDownload,
    isExporting,
    dimensionalConfig: DIMENSIONAL_CONFIG,
    dimensionalFilter,
    onAdvancedSearch,
    bulkActions,
    tableSelectionKey,
    onSelectionChange,
    selectedMarks,
    isRowSelectable: isMarkAdjustable,
  };
}

export default useMarksReport;
